import { useState } from 'react';

import { useAtom } from 'jotai';
import { isEmpty } from 'lodash-es';

import {
  AddSearchRequest,
  ELASTIC_FETCH_LIMIT,
  SearchRequest,
  Filter,
  RangeAdParams,
  CheckEqualSearchResponse,
} from '@/api/elasticsearch/models';
import {
  newSaveSearchApi,
  verifySaveSearchApi,
} from '@/api/fetch/save-search-api';
import { pageLoading, PageLoadingAction } from '@/lib/store';

import { UserDataAtom } from '../store/grpc-free-store';
import {
  Filter as FilterStore,
  FilterAtom,
  SearchQueryAtom,
} from '../store/store';

export const useSaveSearchHook = () => {
  const [filter] = useAtom(FilterAtom);
  const [searchQueryStore] = useAtom(SearchQueryAtom);
  const [, setIsLoading] = useAtom(pageLoading);
  const [disabledSaveSearchBtn, setDisabledSaveSearchBtn] = useState(true);
  const [userData] = useAtom(UserDataAtom);

  const newSaveSearchFn = async () => {
    setIsLoading(PageLoadingAction.Start);
    const addNewSearch: AddSearchRequest = {
      user_id: userData.kcid || '',
      name: searchQueryStore,
    };

    const searchRequest: SearchRequest = {};

    const filterRequest: Filter = {
      category_id: '',
      sub_category_id: '',
      ad_params: {},
      range_ad_params: {},
      governorate: '',
      delegation: [],
      min_price: 0,
      max_price: 0,
      product_type: [],
      level: 0,
      state: 0,
      user_id: '',
      ids: [],
      category_name: '',
      sub_category_name: '',
    };

    if (!isEmpty(filter?.delegation))
      filterRequest.delegation = filter?.delegation!;

    if (!isEmpty(filter?.governorate))
      filterRequest.governorate = filter?.governorate!;

    if (filter?.maxPrice! > 0) filterRequest.max_price = filter?.maxPrice!;
    if (filter?.minPrice! > 0) filterRequest.min_price = filter?.minPrice!;
    if (!isEmpty(filter?.productType))
      filterRequest.product_type = filter?.productType!;

    if (!isEmpty(filter?.adParams)) {
      Object.keys(filter?.adParams).forEach((k) => {
        filterRequest.ad_params![k] = filter?.adParams[k]!;
      });
    }

    if (filter?.rangeAdParams! && !isEmpty(filter?.rangeAdParams)) {
      Object.keys(filter?.rangeAdParams).forEach((k) => {
        if (filter?.rangeAdParams![k]) {
          const rangePrams: RangeAdParams = {
            min: '',
            max: '',
          };
          if (filter?.rangeAdParams[k]?.selectedMin)
            rangePrams.min = filter?.rangeAdParams[k]?.selectedMin || '0';
          if (
            filter?.rangeAdParams[k] &&
            !isEmpty(filter?.rangeAdParams[k]?.selectedMax)
          )
            rangePrams.max = filter?.rangeAdParams[k]?.selectedMax || '0';
          filterRequest.range_ad_params![k] = rangePrams;
        }
      });
    }

    filterRequest.state = 2;
    if (!isEmpty(filter?.subCategoryName))
      filterRequest.sub_category_name = filter.subCategoryName!;

    if (!isEmpty(filter?.subCategoryIds))
      filterRequest.sub_category_id = filter.subCategoryIds![0]!;

    if (!isEmpty(filter?.categoryName))
      filterRequest.category_name = filter.categoryName!;

    if (!isEmpty(filter?.categoryId))
      filterRequest.category_id = filter.categoryId!;
    searchRequest.filter = filterRequest;
    searchRequest.limit = ELASTIC_FETCH_LIMIT;
    searchRequest.offset = 0;
    searchRequest.query = searchQueryStore;

    if (!isEmpty(filterRequest))
      addNewSearch.elasticsearch_filter = searchRequest;

    const saveSearchResponse = await newSaveSearchApi(addNewSearch);

    setIsLoading(PageLoadingAction.Stop);

    return saveSearchResponse;
  };

  const verifySaveSearchUnique = async (f?: FilterStore, q?: string) => {
    const newFilter = f || filter;
    const newSearchQuery = q || searchQueryStore;

    setIsLoading(PageLoadingAction.Start);

    const addNewSearch: AddSearchRequest = {
      user_id: userData.kcid || '',
      name: newSearchQuery,
    };

    const searchRequest: SearchRequest = {};

    const filterRequest: Filter = {
      category_id: '',
      sub_category_id: '',
      ad_params: {},
      range_ad_params: {},
      governorate: '',
      delegation: [],
      min_price: 0,
      max_price: 0,
      product_type: [],
      level: 0,
      state: 0,
      user_id: '',
      ids: [],
      category_name: '',
      sub_category_name: '',
    };

    if (!isEmpty(newFilter?.delegation))
      filterRequest.delegation = newFilter?.delegation!;

    if (!isEmpty(newFilter?.governorate))
      filterRequest.governorate = newFilter?.governorate!;

    if (newFilter?.maxPrice! > 0)
      filterRequest.max_price = newFilter?.maxPrice!;
    if (newFilter?.minPrice! > 0)
      filterRequest.min_price = newFilter?.minPrice!;
    if (!isEmpty(newFilter?.productType))
      filterRequest.product_type = newFilter?.productType!;

    if (!isEmpty(newFilter?.adParams)) {
      Object.keys(newFilter?.adParams).forEach((k) => {
        filterRequest.ad_params![k] = newFilter?.adParams[k]!;
      });
    }

    if (newFilter?.rangeAdParams! && !isEmpty(newFilter?.rangeAdParams)) {
      Object.keys(newFilter?.rangeAdParams).forEach((k) => {
        if (newFilter?.rangeAdParams![k]) {
          const rangePrams: RangeAdParams = {
            min: '',
            max: '',
          };
          if (newFilter?.rangeAdParams[k]?.selectedMin)
            rangePrams.min = newFilter?.rangeAdParams[k]?.selectedMin || '0';
          if (
            newFilter?.rangeAdParams[k] &&
            !isEmpty(newFilter?.rangeAdParams[k]?.selectedMax)
          )
            rangePrams.max = newFilter?.rangeAdParams[k]?.selectedMax || '0';
          filterRequest.range_ad_params![k] = rangePrams;
        }
      });
    }

    filterRequest.state = 2;
    if (!isEmpty(newFilter?.subCategoryName))
      filterRequest.sub_category_name = newFilter.subCategoryName!;

    if (!isEmpty(newFilter?.subCategoryIds))
      filterRequest.sub_category_id = newFilter.subCategoryIds![0]!;

    if (!isEmpty(newFilter?.categoryName))
      filterRequest.category_name = newFilter.categoryName!;

    if (!isEmpty(newFilter?.categoryId))
      filterRequest.category_id = newFilter.categoryId!;
    searchRequest.filter = filterRequest;
    searchRequest.limit = ELASTIC_FETCH_LIMIT;
    searchRequest.offset = 0;
    searchRequest.query = newSearchQuery;

    if (!isEmpty(filterRequest))
      addNewSearch.elasticsearch_filter = searchRequest;

    const saveSearchResponse: CheckEqualSearchResponse =
      await verifySaveSearchApi(addNewSearch);

    setIsLoading(PageLoadingAction.Stop);

    switch (saveSearchResponse.equal) {
      case true:
        setDisabledSaveSearchBtn(true);

        break;
      case false:
        setDisabledSaveSearchBtn(false);

        break;
      default:
        setDisabledSaveSearchBtn(true);

        break;
    }
  };

  return { newSaveSearchFn, verifySaveSearchUnique, disabledSaveSearchBtn };
};
